import { map, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { InspectionPlugin } from '../models/index';
import { BasePlugin } from './base-plugin.service';
import { LanguageService } from './language.service';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { AppConstants } from '../../utils/appConstants.class';
import { ApiMode } from '../../utils/enums';
import { OfflineApiService } from './offlineApi.service';
import { InspectionFactoryService } from './inspectionFactory.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionPluginService extends BasePlugin {
  plugins: InspectionPlugin[] = [];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    protected offlineApi?: OfflineApiService,
    protected factory?: InspectionFactoryService
  ) {
    super();
  }

  public getPlugins(inspectionId: string, sectionId: string): InspectionPlugin[] {
    return this.plugins;
  }

  public getPluginModels(useIn: string = 'point', all: boolean = false): Observable<any> {
    const url = '/plugins/models/' + useIn + '/' + all;
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.pluginModels).pipe(
      map((plugins) => {
        return plugins.filter((p) => p.UseIn.includes(useIn));
      })
    );
  }

  public getAllPlugins(): Observable<any> {
    return this.getPluginModels('point', true);
  }

  getPluginsByApplicationSubType(useIn: string, applicationSubType: number) {
    return this.getPluginModels(useIn).pipe(
      map((plugins) => plugins.filter((p) => p.ApplicationSubType?.includes(applicationSubType)))
    );
  }

  public addPlugin(inspectionId: string, pointId: string, pluginTypes: string[]): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/models/postadd`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.addPlugins(inspectionId, pointId, pluginTypes).pipe(
        map((plugins: InspectionPlugin[]) => {
          return this.dataService.addPlugin(inspectionId, plugins);
        })
      );
    }
    return this.apiService.post(url, pluginTypes).pipe(
      flatMap((plugins: InspectionPlugin[]) => {
        if (this.isManager) {
          return of(this.dataService.addPlugin(inspectionId, plugins));
        }
        return this.factory.addServerPlugins(inspectionId, pointId, plugins).pipe(
          map((res) => {
            return this.dataService.addPlugin(inspectionId, plugins);
          })
        );
      })
    );
  }

  public getPlugin(inspectionId: string, pluginId: string): Observable<InspectionPlugin> {
    return this.dataService.getPlugin(inspectionId, pluginId);
  }

  public deletePlugin(inspectionId: string, pointId: string, pluginId: string): Observable<any> {
    let url = `/inspections/${inspectionId}`;

    url += !pointId || pointId === '-1' || pointId === 'null' ? `/plugins/${pluginId}` : `/points/${pointId}/plugins/${pluginId}`;
    return this.delete(inspectionId, pointId, pluginId).pipe(
      map((success: boolean) => {
        if (success) {
          this.dataService.removePlugin(inspectionId, pluginId);
        }
        return success;
      })
    );
  }

  private delete(inspectionId: string, pointId: string, pluginId: string): Observable<any> {
    let url = `/inspections/${inspectionId}`;

    url += !pointId || pointId === '-1' || pointId === 'null' ? `/plugins/${pluginId}` : `/points/${pointId}/plugins/${pluginId}`;

    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.deletePlugin(inspectionId, pointId, pluginId);
    } else {
      return this.apiService.delete(url);
    }
  }

  public updatePluginsPostUrl(inspectionId: string, plugins: InspectionPlugin[]) {
    const pluginsToUpdate = [];
    (plugins || []).forEach((plugin) => {
      if (plugin.pluginType === 'pointparts') {
        const parts = plugin.inspection.plugins.find((p) => p.pluginType === 'parts');
        if (parts && !parts.postServerUrl) {
          parts.postServerUrl = AppConstants.PLUGINS_URL[parts.pluginType](parts);
          pluginsToUpdate.push(parts);
        }
        return;
      }
      if (!plugin.postServerUrl && AppConstants.PLUGINS_URL[plugin.pluginType]) {
        const url = AppConstants.PLUGINS_URL[plugin.pluginType](plugin);
        plugin.postServerUrl = url;
        pluginsToUpdate.push(plugin);
      }
    });
    if (pluginsToUpdate.length > 0) {
      if (this.isManager) {
        this.dataService.updatePluginUrl(inspectionId, plugins);
        return;
      }
      this.dataService.updatePluginUrl(inspectionId, plugins);
      this.factory.updatePluginUrl(inspectionId, pluginsToUpdate);
    }
  }

  /**
   * translate the label field of keys items and push them to values array
   * E.g., copyTranslationValues({ label: 'plugins.xxx.minutes' }, emptyArray, 'fr')
   * will subscribe the translation service and fill the emptyArray with translated labels { label: 'Minutes' }
   * WARNING : this method uses subscription, so the values array will be filled asynchronously
   *    Order is not guaranteed!
   * [Obsolete] : do not use it anymore, write a new method to return an observable of translated labels
   */
  protected copyTranslationValues(keys, values, language: LanguageService) {
    keys.forEach((key) =>
      language.getValueByKey(key.label).subscribe((value) => {
        const keyValue = { ...key };
        keyValue.label = value;
        values.push(keyValue);
      })
    );
  }

  public getPluginEntity(inspectionId: string, pointId: string, pluginId: string): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/entity`;
    return this.apiService.get(url);
  }

  public updatePluginEntity(inspectionId: string, pointId: string, pluginId: string, plugin: any): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/entity`;
    return this.apiService.put(url, plugin);
  }

  public updatePluginProperties(inspectionId: string, pointId: string, pluginId: string, plugin: any): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/edit`;
    return this.apiService.patch(url, plugin);
  }

  public getPluginFromServer(
    inspectionId: string,
    pluginId: string,
    headers?: { [header: string]: string | string[] }
  ): Observable<InspectionPlugin> {
    const url = `/inspections/${inspectionId}/points/null/plugins/${pluginId}`;
    return this.apiService.get(url, headers);
  }
}
